import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, useMutation, gql } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const GET_HOST_RATINGS = gql`
  query GetHostRatings($hostId: ID!, $limit: Int, $offset: Int) {
    getHostRatings(hostId: $hostId, limit: $limit, offset: $offset) {
      summary {
        averageRating
        totalRatings
        ratingDistribution {
          fiveStars
          fourStars
          threeStars
          twoStars
          oneStar
        }
      }
      reviews {
        rating {
          id
          bookingId
          guestRating {
            general {
              score
            }
            maintenance {
              score
              improve
            }
            cleanliness {
              score
              improve
            }
            communication {
              score
              improve
            }
            convenience {
              score
              improve
            }
            accuracy {
              score
              improve
            }
            comment
          }
          guestRatedAt
          hostReply
          hostRepliedAt
        }
        guestName
        guestAvatarUrl
        tripDate
        carName
      }
      totalCount
    }
  }
`;

const SUBMIT_HOST_REPLY = gql`
  mutation SubmitHostReply($input: SubmitHostReplyInput!) {
    submitHostReply(input: $input) {
      id
      hostReply
      hostRepliedAt
    }
  }
`;

const Container = styled.div`
  ${tw`w-full max-w-full min-w-0 max-w-screen-xl mx-auto px-4 md:px-6 lg:px-0 py-8 box-border`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold mb-8`}
`;

const FiltersRow = styled.div`
  ${tw`flex gap-3 mb-8`}
`;

const FilterButton = styled.button`
  ${tw`px-4 py-2 border border-border-default rounded-lg text-sm flex items-center gap-2 hover:bg-bg-secondary cursor-pointer`}
`;

const MainContent = styled.div`
  ${tw`flex flex-col lg:flex-row gap-8`}
`;

const LeftColumn = styled.div`
  ${tw`lg:w-1/3`}
`;

const RightColumn = styled.div`
  ${tw`lg:w-2/3`}
`;

const RatingsCard = styled.div`
  ${tw`bg-white rounded-lg border border-border-default p-6 mb-6`}
`;

const CardTitle = styled.h2`
  ${tw`text-lg font-semibold mb-1`}
`;

const CardSubtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-4`}
`;

const OverallRow = styled.div`
  ${tw`flex items-center justify-between py-3 border-b border-border-default`}
`;

const OverallLabel = styled.span`
  ${tw`text-text-secondary`}
`;

const OverallPercent = styled.span`
  ${tw`text-text-secondary font-medium`}
`;

const DistributionContainer = styled.div`
  ${tw`mt-4`}
`;

const DistributionRow = styled.div`
  ${tw`flex items-center gap-3 py-1`}
`;

const DistributionLabel = styled.span`
  ${tw`text-sm text-text-secondary w-16`}
`;

const DistributionBar = styled.div`
  ${tw`flex-1 h-2 bg-bg-secondary rounded-full overflow-hidden`}
`;

const DistributionFill = styled.div<{ $width: number; $color: string }>`
  ${tw`h-full rounded-full`}
  width: ${({ $width }) => $width}%;
  background-color: ${({ $color }) => $color};
`;

const DistributionPercent = styled.span`
  ${tw`text-sm text-text-secondary w-10 text-right`}
`;

const StatsRow = styled.div`
  ${tw`flex gap-6 mt-6 pt-4 border-t border-border-default`}
`;

const StatItem = styled.div`
  ${tw`text-center`}
`;

const StatLabel = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

const StatValue = styled.div`
  ${tw`text-lg font-semibold`}
`;

const CategoryCard = styled.div<{ $isExpanded: boolean }>`
  ${tw`bg-white rounded-lg border border-border-default p-4 mb-3 cursor-pointer`}
`;

const CategoryHeader = styled.div`
  ${tw`flex items-center justify-between`}
`;

const CategoryName = styled.span`
  ${tw`font-medium`}
`;

const CategoryPercent = styled.span`
  ${tw`flex items-center gap-2 text-text-secondary`}
`;

const ImprovementTags = styled.div`
  ${tw`mt-4 flex flex-wrap gap-2`}
`;

const ImprovementTag = styled.span`
  ${tw`px-3 py-1 bg-bg-secondary text-text-secondary text-sm rounded-full`}
`;

const ReviewsHeader = styled.div`
  ${tw`flex items-center justify-between mb-4`}
`;

const ReviewsTitle = styled.h2`
  ${tw`text-lg font-semibold`}
`;

const SortDropdown = styled.select`
  ${tw`px-3 py-2 border border-border-default rounded-lg text-sm`}
`;

const ReviewCard = styled.div`
  ${tw`bg-white rounded-lg border border-border-default p-4 mb-4`}
`;

const ReviewHeader = styled.div`
  ${tw`flex items-start gap-3`}
`;

const Avatar = styled.div`
  ${tw`w-10 h-10 rounded-full bg-bg-secondary flex items-center justify-center text-text-secondary font-medium text-sm overflow-hidden flex-shrink-0`}
`;

const ReviewMeta = styled.div`
  ${tw`flex-1`}
`;

const ReviewStars = styled.div`
  ${tw`flex gap-1 mb-1`}
`;

const ReviewerInfo = styled.div`
  ${tw`text-sm`}
`;

const ReviewerName = styled.span`
  ${tw`text-text-secondary font-medium`}
`;

const ReviewDate = styled.span`
  ${tw`text-text-tertiary ml-1`}
`;

const CarInfo = styled.p`
  ${tw`text-sm text-text-secondary mt-1`}
`;

const ReviewComment = styled.p`
  ${tw`text-text-secondary text-sm mt-3`}
`;

const HostReplyContainer = styled.div`
  ${tw`mt-4 p-4 bg-bg-secondary rounded-lg`}
`;

const HostReplyLabel = styled.p`
  ${tw`text-sm font-semibold text-text-secondary mb-2`}
`;

const HostReplyText = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const ReviewActions = styled.div`
  ${tw`flex justify-end gap-4 mt-4`}
`;

const ActionLink = styled.button`
  ${tw`text-sm text-text-secondary hover:text-text-secondary cursor-pointer`}
`;

const ReplyForm = styled.div`
  ${tw`mt-4`}
`;

const ReplyTextarea = styled.textarea`
  ${tw`w-full px-4 py-3 border border-border-default rounded-lg resize-none focus:ring-2 focus:ring-gray-400 focus:border-transparent`}
  height: 100px;
`;

const ReplyActions = styled.div`
  ${tw`flex justify-end gap-3 mt-3`}
`;

const CancelButton = styled.button`
  ${tw`px-4 py-2 text-text-secondary hover:text-text-primary cursor-pointer`}
`;

const SubmitReplyButton = styled.button`
  ${tw`px-4 py-2 bg-black text-white rounded-lg hover:bg-black cursor-pointer`}
`;

const SeeMoreButton = styled.button`
  ${tw`w-full py-3 bg-bg-secondary text-text-secondary font-medium rounded-lg hover:bg-bg-secondary cursor-pointer`}
`;

const NoRatingsText = styled.p`
  ${tw`text-text-secondary text-center py-8`}
`;

// Improvement tag labels
const IMPROVEMENT_LABELS: Record<string, string> = {
  WINDSHIELD_WIPERS: 'Windshield wipers',
  LOW_TIRE_PRESSURE: 'Low tire pressure',
  BRAKES: 'Brakes',
  MIRRORS: 'Mirrors',
  LOW_CAR_BATTERY: 'Low car battery',
  TIRE_THREADS: 'Low tire tread',
  CHECK_ENGINE_LIGHT: 'Check engine light',
  LOW_OIL: 'Low oil light',
  INTERIOR_DIRTY: 'Dirty interior',
  EXTERIOR_DIRTY: 'Dirty exterior',
  UPHOLSTERY_STAINS: 'Upholstery stains',
  PET_HAIR: 'Pet hair',
  TRASH_OR_CLUTTER: 'Trash or clutter',
  UNPLEASANT_SMELL: 'Unpleasant smell',
  PHOTOS_INACCURATE: 'Photos',
  NOT_RESPONSIVE: 'Not responsive',
  SLOW_RESPONSE: 'Slow response',
  CAR_LOCATION_ISSUE: 'Car location',
  OTHERS: 'Other',
};

interface HostRatingsDashboardProps {
  hostId: string;
}

export const HostRatingsDashboard = ({ hostId }: HostRatingsDashboardProps) => {
  const [limit, setLimit] = useState(10);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const { data, loading, error, fetchMore, refetch } = useQuery(GET_HOST_RATINGS, {
    variables: { hostId, limit, offset: 0 },
    skip: !hostId,
  });

  const [submitHostReply, { loading: submittingReply }] = useMutation(SUBMIT_HOST_REPLY, {
    onCompleted: () => {
      toast.success('Reply submitted successfully');
      setReplyingTo(null);
      setReplyText('');
      refetch();
    },
    onError: error => {
      toast.error(error.message || 'Failed to submit reply');
    },
  });

  if (loading) {
    return (
      <Container>
        <Title>Ratings & reviews</Title>
        <NoRatingsText>Loading ratings...</NoRatingsText>
      </Container>
    );
  }

  if (error || !data?.getHostRatings) {
    return (
      <Container>
        <Title>Ratings & reviews</Title>
        <NoRatingsText>Failed to load ratings</NoRatingsText>
      </Container>
    );
  }

  const { summary, reviews, totalCount } = data.getHostRatings;

  // Calculate category percentages from all reviews
  const calculateCategoryStats = () => {
    const stats: Record<
      string,
      { total: number; count: number; improvements: Record<string, number> }
    > = {
      maintenance: { total: 0, count: 0, improvements: {} },
      cleanliness: { total: 0, count: 0, improvements: {} },
      communication: { total: 0, count: 0, improvements: {} },
      convenience: { total: 0, count: 0, improvements: {} },
      accuracy: { total: 0, count: 0, improvements: {} },
    };

    reviews.forEach((review: any) => {
      const gr = review.rating.guestRating;
      if (!gr) return;

      ['maintenance', 'cleanliness', 'communication', 'convenience', 'accuracy'].forEach(cat => {
        const catRating = gr[cat];
        if (catRating?.score) {
          stats[cat].total += catRating.score;
          stats[cat].count++;

          if (catRating.improve) {
            catRating.improve.forEach((imp: string) => {
              stats[cat].improvements[imp] = (stats[cat].improvements[imp] || 0) + 1;
            });
          }
        }
      });
    });

    return Object.entries(stats).map(([key, value]) => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
      percent: value.count > 0 ? Math.round((value.total / (value.count * 5)) * 100) : 0,
      improvements: Object.entries(value.improvements)
        .map(([imp, count]) => ({ label: IMPROVEMENT_LABELS[imp] || imp, count }))
        .sort((a, b) => b.count - a.count),
    }));
  };

  const categoryStats = calculateCategoryStats();

  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSubmitReply = (bookingId: string) => {
    if (!replyText.trim()) return;
    submitHostReply({
      variables: {
        input: {
          bookingId,
          reply: replyText.trim(),
        },
      },
    });
  };

  const handleSeeMore = () => {
    const newLimit = limit + 10;
    setLimit(newLimit);
    fetchMore({
      variables: { hostId, limit: newLimit, offset: 0 },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  };

  const distribution = summary.ratingDistribution;
  const totalWithRatings =
    distribution.fiveStars +
    distribution.fourStars +
    distribution.threeStars +
    distribution.twoStars +
    distribution.oneStar;
  const overallPercent =
    totalWithRatings > 0 ? Math.round((distribution.fiveStars / totalWithRatings) * 100) : 0;

  return (
    <Container>
      <Title>Ratings & reviews</Title>

      <FiltersRow>
        <FilterButton>
          All listings <FontAwesomeIcon icon={faChevronDown} size="xs" />
        </FilterButton>
        <FilterButton>
          Last 365 days <FontAwesomeIcon icon={faChevronDown} size="xs" />
        </FilterButton>
        <FilterButton>
          Overall <FontAwesomeIcon icon={faChevronDown} size="xs" />
        </FilterButton>
      </FiltersRow>

      <MainContent>
        <LeftColumn>
          <RatingsCard>
            <CardTitle>Ratings</CardTitle>
            <CardSubtitle>Percentage of 5-star ratings, including unrated trips</CardSubtitle>

            <OverallRow>
              <OverallLabel>Overall</OverallLabel>
              <OverallPercent>{overallPercent}%</OverallPercent>
            </OverallRow>

            <DistributionContainer>
              {[
                { label: '5 stars', count: distribution.fiveStars, color: '#C9B896' },
                { label: '4 stars', count: distribution.fourStars, color: '#C9B896' },
                { label: '3 stars', count: distribution.threeStars, color: '#C9B896' },
                { label: '2 stars', count: distribution.twoStars, color: '#C9B896' },
                { label: '1 star', count: distribution.oneStar, color: '#C9B896' },
              ].map(item => {
                const percent =
                  totalWithRatings > 0 ? Math.round((item.count / totalWithRatings) * 100) : 0;
                return (
                  <DistributionRow key={item.label}>
                    <DistributionLabel>{item.label}</DistributionLabel>
                    <DistributionBar>
                      <DistributionFill $width={percent} $color={item.color} />
                    </DistributionBar>
                    <DistributionPercent>{percent}%</DistributionPercent>
                  </DistributionRow>
                );
              })}
            </DistributionContainer>

            <StatsRow>
              <StatItem>
                <StatLabel>Ratings</StatLabel>
                <StatValue>{summary.totalRatings}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Average</StatLabel>
                <StatValue>
                  {summary.averageRating.toFixed(2)}{' '}
                  <FontAwesomeIcon icon={faStar} size="sm" style={{ color: '#C9B896' }} />
                </StatValue>
              </StatItem>
            </StatsRow>
          </RatingsCard>

          {categoryStats.map(cat => (
            <CategoryCard
              key={cat.key}
              $isExpanded={expandedCategories[cat.key]}
              onClick={() => toggleCategory(cat.key)}
            >
              <CategoryHeader>
                <CategoryName>{cat.label}</CategoryName>
                <CategoryPercent>
                  {cat.percent}%
                  <FontAwesomeIcon
                    icon={expandedCategories[cat.key] ? faChevronUp : faChevronDown}
                    size="xs"
                  />
                </CategoryPercent>
              </CategoryHeader>
              {expandedCategories[cat.key] && cat.improvements.length > 0 && (
                <ImprovementTags>
                  {cat.improvements.map(imp => (
                    <ImprovementTag key={imp.label}>
                      {imp.label} ({imp.count})
                    </ImprovementTag>
                  ))}
                </ImprovementTags>
              )}
            </CategoryCard>
          ))}
        </LeftColumn>

        <RightColumn>
          <ReviewsHeader>
            <ReviewsTitle>Reviews ({totalCount})</ReviewsTitle>
            <SortDropdown>
              <option>Sorted by: Most recent</option>
            </SortDropdown>
          </ReviewsHeader>

          {reviews.length === 0 ? (
            <NoRatingsText>No reviews yet</NoRatingsText>
          ) : (
            <>
              {reviews.map((review: any) => (
                <ReviewCard key={review.rating.id}>
                  <ReviewHeader>
                    <Avatar>
                      {review.guestAvatarUrl ? (
                        <img
                          src={review.guestAvatarUrl}
                          alt={review.guestName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        getInitials(review.guestName || 'G')
                      )}
                    </Avatar>
                    <ReviewMeta>
                      <ReviewStars>
                        {[1, 2, 3, 4, 5].map(star => (
                          <FontAwesomeIcon
                            key={star}
                            icon={faStar}
                            style={{
                              color:
                                star <= (review.rating.guestRating?.general?.score || 0)
                                  ? '#C9B896'
                                  : '#E5E7EB',
                              fontSize: '14px',
                            }}
                          />
                        ))}
                      </ReviewStars>
                      <ReviewerInfo>
                        <ReviewerName>{review.guestName}</ReviewerName>
                        <ReviewDate>
                          •{' '}
                          {format(
                            new Date(review.tripDate || review.rating.guestRatedAt),
                            'MMMM d, yyyy',
                          )}
                        </ReviewDate>
                      </ReviewerInfo>
                      {review.carName && <CarInfo>{review.carName}</CarInfo>}
                    </ReviewMeta>
                  </ReviewHeader>

                  {review.rating.guestRating?.comment && (
                    <ReviewComment>{review.rating.guestRating.comment}</ReviewComment>
                  )}

                  {review.rating.hostReply && (
                    <HostReplyContainer>
                      <HostReplyLabel>Your response</HostReplyLabel>
                      <HostReplyText>{review.rating.hostReply}</HostReplyText>
                    </HostReplyContainer>
                  )}

                  {replyingTo === review.rating.bookingId ? (
                    <ReplyForm>
                      <ReplyTextarea
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        placeholder="Write your response..."
                      />
                      <ReplyActions>
                        <CancelButton
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                          }}
                        >
                          Cancel
                        </CancelButton>
                        <SubmitReplyButton
                          onClick={() => handleSubmitReply(review.rating.bookingId)}
                          disabled={submittingReply || !replyText.trim()}
                        >
                          {submittingReply ? 'Submitting...' : 'Submit'}
                        </SubmitReplyButton>
                      </ReplyActions>
                    </ReplyForm>
                  ) : (
                    <ReviewActions>
                      <ActionLink>See full review</ActionLink>
                      {!review.rating.hostReply && (
                        <ActionLink onClick={() => setReplyingTo(review.rating.bookingId)}>
                          Leave a public response
                        </ActionLink>
                      )}
                    </ReviewActions>
                  )}
                </ReviewCard>
              ))}

              {totalCount > reviews.length && (
                <SeeMoreButton onClick={handleSeeMore}>See more reviews</SeeMoreButton>
              )}
            </>
          )}
        </RightColumn>
      </MainContent>
    </Container>
  );
};
